import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';
import type { RouterOutput } from '@trapeze/server';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

type ProviderRule = RouterOutput['admin']['getProviderRules']['rules'][number];

interface EditProviderRuleDialogProps {
  rule: ProviderRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  organizationId: string;
  ehrSystem: 'imedicware';
  providerId?: string;
  providerName?: string;
}

export function EditProviderRuleDialog({
  rule,
  open,
  onOpenChange,
  onSave,
  organizationId,
  ehrSystem,
  providerId: externalProviderId,
  providerName: externalProviderName,
}: EditProviderRuleDialogProps) {
  const [providerId, setProviderId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [ruleType, setRuleType] = useState<'ALLOW' | 'DENY'>('ALLOW');
  const [entityType, setEntityType] = useState<'CONDITION' | 'INTENT' | 'PROCEDURE' | null>(null);
  const [canonicalTag, setCanonicalTag] = useState('');
  const [error, setError] = useState('');

  // Determine if we're in provider-centric mode (providerId passed from parent)
  const isProviderCentric = !!externalProviderId;

  // Fetch providers only if not in provider-centric mode
  const providersQuery = trpc.admin.getProviders.useQuery(
    { organizationId, ehrSystem },
    { enabled: !!organizationId && open && !isProviderCentric }
  );

  // Fetch canonical tags based on entity type
  const tagsQuery = trpc.admin.getCanonicalTagsByCategory.useQuery(
    { organizationId, ehrSystem, category: entityType ?? 'CONDITION' },
    { enabled: !!organizationId && entityType !== null && open }
  );

  // Mutations
  const createMutation = trpc.admin.createProviderRule.useMutation({
    onSuccess: () => {
      toast.success('Rule created successfully');
      onOpenChange(false);
      onSave();
      setError('');
    },
    onError: (error) => {
      toast.error(`Failed to create rule: ${error.message}`);
      setError(error.message);
    },
  });

  const updateMutation = trpc.admin.updateProviderRule.useMutation({
    onSuccess: () => {
      toast.success('Rule updated successfully');
      onOpenChange(false);
      onSave();
      setError('');
    },
    onError: (error) => {
      toast.error(`Failed to update rule: ${error.message}`);
      setError(error.message);
    },
  });

  // Reset form when dialog opens or rule changes
  useEffect(() => {
    if (open) {
      if (rule) {
        // Editing existing rule
        setProviderId(rule.providerId);
        setRuleLabel(rule.ruleLabel);
        setRuleType(rule.ruleType as 'ALLOW' | 'DENY');
        setEntityType(rule.entityType as 'CONDITION' | 'INTENT' | 'PROCEDURE');
        setCanonicalTag(rule.canonicalTag);
      } else {
        // Creating new rule
        setProviderId(externalProviderId || '');
        setRuleLabel('');
        setRuleType('ALLOW');
        setEntityType(null);
        setCanonicalTag('');
      }
      setError('');
    }
  }, [rule, open, externalProviderId]);

  // Clear canonical tag when entity type changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: entityType is intentionally the only dependency
  useEffect(() => {
    setCanonicalTag('');
  }, [entityType]);

  const handleSave = () => {
    // Validate required fields
    if (!providerId || !ruleLabel || !ruleType || !entityType || !canonicalTag) {
      setError('All fields are required');
      return;
    }

    // After validation, entityType is guaranteed to be non-null
    if (rule) {
      // Update existing rule
      const updates: {
        ruleLabel?: string;
        ruleType?: 'ALLOW' | 'DENY';
        entityType?: 'CONDITION' | 'INTENT' | 'PROCEDURE';
        canonicalTag?: string;
      } = {};

      if (ruleLabel !== rule.ruleLabel) updates.ruleLabel = ruleLabel;
      if (ruleType !== rule.ruleType) updates.ruleType = ruleType;
      if (entityType !== rule.entityType) updates.entityType = entityType;
      if (canonicalTag !== rule.canonicalTag) updates.canonicalTag = canonicalTag;

      if (Object.keys(updates).length > 0) {
        updateMutation.mutate({ organizationId, ruleId: rule.id, ...updates });
      } else {
        // No changes - close dialog without making API call
        toast.success('No changes to save');
        onOpenChange(false);
        onSave();
      }
    } else {
      // Create new rule
      createMutation.mutate({
        organizationId,
        providerId,
        ruleLabel,
        ruleType,
        entityType,
        canonicalTag,
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Check if form has required fields filled
  const isValid = providerId && ruleLabel && ruleType && entityType && canonicalTag;

  // Get selected tag details for display
  const selectedTag = tagsQuery.data?.find((tag) => tag.tagKey === canonicalTag);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit Provider Rule' : 'Create Provider Rule'}</DialogTitle>
          <DialogDescription>
            {rule
              ? `Update rule for ${rule.providerName}`
              : 'Configure which patients a provider can see based on conditions, intents, or procedures'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Provider Selection */}
          <div className="space-y-2">
            <Label htmlFor="provider">Provider *</Label>
            {rule || isProviderCentric ? (
              <div className="flex items-center gap-2 min-h-10">
                <Badge variant="outline" className="text-sm">
                  {rule?.providerName || externalProviderName}
                </Badge>
                {rule && (
                  <span className="text-xs text-muted-foreground">
                    (Cannot change provider for existing rule)
                  </span>
                )}
              </div>
            ) : (
              <Select value={providerId} onValueChange={setProviderId}>
                <SelectTrigger id="provider" className="w-full">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providersQuery.data?.providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Rule Label */}
          <div className="space-y-2">
            <Label htmlFor="ruleLabel">Rule Label *</Label>
            <Input
              id="ruleLabel"
              placeholder="e.g., 'Retina specialist only'"
              value={ruleLabel}
              onChange={(e) => setRuleLabel(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              A descriptive name to help you identify this rule
            </p>
          </div>

          {/* Rule Type */}
          <div className="space-y-2">
            <Label htmlFor="ruleType">Rule Type *</Label>
            <Select
              value={ruleType}
              onValueChange={(value) => setRuleType(value as 'ALLOW' | 'DENY')}
            >
              <SelectTrigger id="ruleType" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALLOW">
                  <span className="font-medium">ALLOW</span>
                  <span className="text-muted-foreground">- Whitelist (restrictive)</span>
                </SelectItem>
                <SelectItem value="DENY">
                  <span className="font-medium">DENY</span>
                  <span className="text-muted-foreground">- Blacklist (exclusion)</span>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="space-y-1.5 text-xs text-muted-foreground mt-1.5">
              <p>
                <strong className="text-foreground">ALLOW (Whitelist):</strong> Provider can ONLY
                see patients with ALLOW tags. All others hidden.
              </p>
              <p>
                <strong className="text-foreground">DENY (Blacklist):</strong> Provider sees all
                patients EXCEPT those with DENY tags.
              </p>
            </div>
            <div className="mt-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-md border border-amber-200">
              <strong>Important:</strong> If you add even one ALLOW rule, this provider will{' '}
              <strong>only</strong> see patients matching those tags. Use DENY to exclude specific
              patients while seeing everyone else.
            </div>
          </div>

          {/* Entity Type */}
          <div className="space-y-2">
            <Label htmlFor="entityType">Entity Type *</Label>
            <Select
              value={entityType ?? ''}
              onValueChange={(value) =>
                setEntityType(value as 'CONDITION' | 'INTENT' | 'PROCEDURE')
              }
            >
              <SelectTrigger id="entityType" className="w-full">
                <SelectValue placeholder="Select entity type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CONDITION">
                  <span className="font-medium">CONDITION</span>
                  <span className="text-muted-foreground text-xs ml-2">
                    (Medical conditions/symptoms)
                  </span>
                </SelectItem>
                <SelectItem value="INTENT">
                  <span className="font-medium">INTENT</span>
                  <span className="text-muted-foreground text-xs ml-2">
                    (Primary appointment goals)
                  </span>
                </SelectItem>
                <SelectItem value="PROCEDURE">
                  <span className="font-medium">PROCEDURE</span>
                  <span className="text-muted-foreground text-xs ml-2">
                    (Specific medical procedures)
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Canonical Tag */}
          <div className="space-y-2">
            <Label htmlFor="canonicalTag">Canonical Tag *</Label>
            {!entityType ? (
              <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md border">
                Select an entity type first to see available tags
              </div>
            ) : tagsQuery.isLoading ? (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md border">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Loading tags...</span>
              </div>
            ) : tagsQuery.data && tagsQuery.data.length > 0 ? (
              <>
                <Select value={canonicalTag} onValueChange={setCanonicalTag}>
                  <SelectTrigger id="canonicalTag" className="w-full">
                    <SelectValue placeholder="Select a tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {tagsQuery.data.map((tag) => (
                      <SelectItem key={tag.tagKey} value={tag.tagKey}>
                        {tag.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTag?.description && (
                  <p className="text-xs text-muted-foreground mt-1.5">{selectedTag.description}</p>
                )}
              </>
            ) : (
              <div className="text-sm text-amber-700 p-3 bg-amber-50 rounded-md border border-amber-200">
                No tags available for this entity type
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {rule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
